import type { ComponentProps } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { StatusMessage } from '~/eds/src/components/StatusMessage/StatusMessage';
import { TicketNegotiationStatus } from '~/enums/TicketNegotiationStatus';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';

const messages = defineMessages({
  inProgressTitle: {
    id: 'TicketNegotiationStatus.inProgressTitle',
    defaultMessage: 'With Supplier',
  },
  inProgressBody: {
    id: 'TicketNegotiationStatus.inProgressBody',
    defaultMessage:
      'This document is currently in the Supplier Portal. Approvals, editing, and sharing are disabled until the Supplier sends back the document or the document is retracted.',
  },
  accepted: {
    id: 'TicketNegotiationStatus.accepted',
    defaultMessage: 'Supplier has accepted terms.',
  },
  newDocVersion: {
    id: 'TicketNegotiationStatus.newDocVersion',
    defaultMessage: 'New document version received from Supplier',
  },
  failedTitle: {
    id: 'TicketNegotiationStatus.failedTitle',
    defaultMessage: 'Connection failed',
  },
  failedBody: {
    id: 'TicketNegotiationStatus.failedBody',
    defaultMessage:
      'We could not connect to the Supplier Portal. Try submitting the document again.',
  },
});

interface NegotiationStatusContent {
  title?: string;
  message: string;
  status?: ComponentProps<typeof StatusMessage>['status'];
}

type TicketNegotiationStatusDisplayProps = {
  status: TicketNegotiationStatus;
};

function useStatusContentLocalizationMessages(): Partial<
  Record<TicketNegotiationStatus, NegotiationStatusContent>
> {
  const intl = useIntl();
  return {
    [TicketNegotiationStatus.InProgress]: {
      title: intl.formatMessage(messages.inProgressTitle),
      message: intl.formatMessage(messages.inProgressBody),
    },
    [TicketNegotiationStatus.Accepted]: {
      message: intl.formatMessage(messages.accepted),
      status: 'success',
    },
    [TicketNegotiationStatus.NewDocVersion]: {
      message: intl.formatMessage(messages.newDocVersion),
      status: 'warning',
    },
    [TicketNegotiationStatus.Failed]: {
      title: intl.formatMessage(messages.failedTitle),
      message: intl.formatMessage(messages.failedBody),
      status: 'danger',
    },
  };
}

export function TicketNegotiationStatusDisplay(
  props: TicketNegotiationStatusDisplayProps,
) {
  const statusContent = useStatusContentLocalizationMessages();
  const isWSSIntegrationM4SupplierNegotiationEnabled = useFlag(
    FlagType.WSSIntegrationM4SupplierNegotiation,
  );

  const content = statusContent[props.status];
  if (!isWSSIntegrationM4SupplierNegotiationEnabled || !content) {
    return null;
  }

  return (
    <StatusMessage
      {...content}
      data-testid={`negotiation-status-${props.status}`}
    />
  );
}
